/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable dot-notation */
/* eslint-disable prefer-const */
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import { UploadOutlined } from '@ant-design/icons'
import { Row, Col, PageHeader, Descriptions, Card, Button, Tag } from 'antd'
import { useRouteMatch, useLocation } from 'react-router-dom'
import { ContentItem, ContentPathEntry } from '@antegma/accallio-cloud-hootsuite-application-api-javacript-sdk'
import Loader from '@antegma/accallio-cloud-react-components/lib/app/components/Loader'
import { TDefaultSagas } from '@antegma/accallio-cloud-react-components/lib/hootsuite/common/redux/interface'
import PrettyBytes from 'react-pretty-bytes'
import FormattedDuration, { TIMER_FORMAT } from 'react-intl-formatted-duration'
import videojs from 'video.js'
import contentSourceApi from '~/services/ContentSource'
import VideoPlayer from '~/components/videoplayer'
import TagList from '~/components/tag-list'
import CantoBreadCrumb from '~/components/canto-breadcrumb'
import { useUploadToComposer } from '~/hooks/useUploadToComposer'
import styles from './styles.module.scss'

const ContentDetailView: React.FunctionComponent = () => {
  const route = useRouteMatch<{ contentId: string }>()
  const location = useLocation<{ breadcrumbPath?: ContentPathEntry[] }>()
  const [contentItem, setContentItem] = useState<ContentItem | undefined>(undefined)
  const oidc = useSelector((state: TDefaultSagas) => state.oidc)
  const { uploadToComposer } = useUploadToComposer()

  const options = { headers: { Authorization: `Bearer ${oidc.user?.access_token}` } }

  // Build breadcrumb path: get folder path from sessionStorage + append current content item
  const getContentPath = (): ContentPathEntry[] | undefined => {
    if (!contentItem) return undefined

    // Get the folder path from sessionStorage (stored by MediaCardView)
    let folderPath = []
    const storedPath = sessionStorage.getItem('contentDetailsBreadcrumbPath')
    if (storedPath) {
      try {
        folderPath = JSON.parse(storedPath)
        console.log('ContentDetails - Folder path from sessionStorage:', folderPath)
      } catch (e) {
        console.error('Failed to parse breadcrumb path:', e)
      }
    }

    // Append the current content item to the folder path
    const finalPath = [...folderPath, { id: contentItem.id, name: contentItem.name }]
    console.log('ContentDetails - Final breadcrumb path:', finalPath)
    return finalPath
  }

  const {
    params: { contentId },
  } = route

  React.useEffect(() => {
    contentSourceApi
      .getContentItem(contentId, options)
      .then((response) => {
        setContentItem(response.data)
        console.log('Content item:', response.data)
        console.log('Content item path:', response.data.path)
      })
      .catch((reason) => {
        console.log(reason)
      })
  }, [contentId])

  const openComposer = (): void => {
    if (contentItem) {
      uploadToComposer(contentItem)
    }
  }

  function Preview(): JSX.Element {
    if (contentItem?.mediaType === 'Image') {
      return (
        <div className={styles.image_preview_container}>
          <img className={styles.image_preview_image} src={contentItem?.urls?.preview} alt={`Preview of ${contentItem.name}`} />
        </div>
      )
    }

    let source = ''

    if (contentItem !== undefined && contentItem?.urls !== undefined && contentItem?.urls.original !== undefined) {
      source = contentItem?.urls?.original
    }

    let videoJsOptions: videojs.PlayerOptions = {
      autoplay: false,
      preload: 'metadata',
      poster: contentItem?.urls?.preview,
      controls: true,
      fluid: true,
      sources: [
        {
          src: source,
        },
      ],
    }

    return <VideoPlayer options={videoJsOptions} />
  }

  function Duration(): JSX.Element {
    if (contentItem !== undefined && contentItem.duration !== undefined) {
      return (
        <Descriptions.Item label="Duration">
          <FormattedDuration seconds={contentItem.duration / 1000} format={TIMER_FORMAT} />
        </Descriptions.Item>
      )
    }
    return <></>
  }

  return contentItem ? (
    <PageHeader title={`${contentItem.name}`}>
      <div style={{ paddingBottom: '10px' }}>
        <CantoBreadCrumb contentPathEntries={getContentPath()} />
      </div>
      <Row gutter={[16, 16]}>
        <Col xs={24} lg={18}>
          <Preview />
        </Col>
        <Col xs={24} lg={6}>
          <Button className={styles.open_composer_btn} type="primary" shape="round" size="large" icon={<UploadOutlined />} onClick={openComposer}>
            Open in Composer
          </Button>
          <Card title="Information" bordered={false}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Name">{contentItem.name}</Descriptions.Item>
              <Descriptions.Item label="Description">{contentItem.description}</Descriptions.Item>
              <Descriptions.Item label="Tags">
                <TagList tags={contentItem.tags} />
              </Descriptions.Item>
              <Descriptions.Item label="Created">{contentItem.created}</Descriptions.Item>
              <Descriptions.Item label="Created By">{contentItem.createdBy}</Descriptions.Item>
              <Descriptions.Item label="Last Modified">{contentItem.lastUpdated}</Descriptions.Item>
              <Descriptions.Item label="Last Modified By">{contentItem.lastUpdatedBy}</Descriptions.Item>
            </Descriptions>
          </Card>
          <Card title="Metadata" bordered={false}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Mime Type">{contentItem.mimeType}</Descriptions.Item>
              <Descriptions.Item label="Width">{contentItem.width}</Descriptions.Item>
              <Descriptions.Item label="Height">{contentItem.height}</Descriptions.Item>
              <Descriptions.Item label="Color">{contentItem.color}</Descriptions.Item>
              <Descriptions.Item label="Size">
                <PrettyBytes bytes={contentItem.size} />
              </Descriptions.Item>
              <Duration />
            </Descriptions>
          </Card>
          <Card title="Copyright & License" bordered={false}>
            <Descriptions size="small" column={1}>
              <Descriptions.Item label="Copyright">{contentItem.copyright}</Descriptions.Item>
              <Descriptions.Item label="License">{contentItem.license}</Descriptions.Item>
              <Descriptions.Item label="Terms & Conditions">{contentItem.termsAndConditions}</Descriptions.Item>
            </Descriptions>
          </Card>
        </Col>
      </Row>
    </PageHeader>
  ) : (
    <Loader />
  )
}

export default {
  name: 'ContentDetailView',
  routeProps: {
    path: '/contentdetails/:contentId',
    component: ContentDetailView,
  },
}
